import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/StatusBadge';
import { Progress } from '@/components/ui/progress';
import { FileText, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

const statusSteps = ['draft', 'submitted', 'approved'];
const statusProgress = { draft: 20, submitted: 50, under_review: 65, correction_needed: 40, approved: 100, rejected: 50 };

export default function TrackStatus() {
  const [apps, setApps] = useState([]);

  useEffect(() => {
    api.get('/applications/my').then(r => setApps(r.data)).catch(() => {});
  }, []);

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return <CheckCircle className="w-5 h-5 text-[#009543]" />;
      case 'rejected': return <XCircle className="w-5 h-5 text-[#CE1021]" />;
      case 'correction_needed': return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'submitted': return <Clock className="w-5 h-5 text-[#007FFF]" />;
      default: return <FileText className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="track-status-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Suivi du Statut</h1>
        <p className="text-muted-foreground text-sm mt-1">Suivez l'avancement de vos dossiers</p>
      </div>

      {apps.length === 0 ? (
        <Card className="border">
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">Aucun dossier a suivre.</p>
          </CardContent>
        </Card>
      ) : (
        apps.map(app => (
          <Card key={app.application_id} className="border" data-testid={`app-status-${app.application_id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getStatusIcon(app.status)}
                  <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {app.personal_info?.full_name || 'Dossier'}
                  </CardTitle>
                </div>
                <StatusBadge status={app.status} />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Progression</span>
                  <span>{statusProgress[app.status] || 0}%</span>
                </div>
                <Progress value={statusProgress[app.status] || 0} className="h-2" />
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Pays de residence</p>
                  <p className="font-medium">{app.personal_info?.country_of_residence || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Cree le</p>
                  <p className="font-medium">{app.created_at ? new Date(app.created_at).toLocaleDateString('fr-FR') : '-'}</p>
                </div>
                {app.submitted_at && (
                  <div>
                    <p className="text-muted-foreground text-xs">Soumis le</p>
                    <p className="font-medium">{new Date(app.submitted_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                )}
                {app.reviewed_at && (
                  <div>
                    <p className="text-muted-foreground text-xs">Examine le</p>
                    <p className="font-medium">{new Date(app.reviewed_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                )}
              </div>

              {app.status === 'rejected' && app.rejection_reason && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-800">Raison du rejet</p>
                  <p className="text-sm text-red-700 mt-1">{app.rejection_reason}</p>
                </div>
              )}

              {app.status === 'correction_needed' && app.correction_notes && (
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm font-medium text-orange-800">Corrections demandees</p>
                  <p className="text-sm text-orange-700 mt-1">{app.correction_notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
